'use client';
import { VStack, Text } from '@chakra-ui/react';
import { useAccount } from 'wagmi';
import {
  DialogRoot,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBackdrop,
  DialogCloseTrigger,
  DialogContent,
} from '@/components/ui/dialog';
import { ModalCloseIcon } from '@/components/ui/svgs/modal-close';
import { ArbitrumIcon } from '@/components/ui/svgs/arbitrum';
import { AmountInputArea } from '@/components/ai-earn/AmountInputArea';
import { useState } from 'react';
import { useErc20TokenBalance } from '@/lib/hooks/walletHooks/useErc20Token';
import { arbitrum } from 'viem/chains';
import { USDC_CONTRACT } from '@/lib/config/contracts';
import { BigNumber } from 'bignumber.js';
import { Button } from '@/components/ui/button';

interface DepositButtonProps {
  trigger?: React.ReactNode;
}

export function DepositButton({ trigger }: DepositButtonProps) {
  const { isConnected, address } = useAccount();
  const [amount, setAmount] = useState('');
  const { transfer, balance } = useErc20TokenBalance({
    contractAddress: USDC_CONTRACT[arbitrum.id],
    userAddress: address,
  });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  return isConnected ? (
    <DialogRoot
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      placement={'center'}
    >
      <DialogBackdrop />
      <DialogTrigger asChild>
        {trigger || (
          <Button
            px={6}
            rounded={8}
            bg="text.t1"
            color="bg.t1"
            fontWeight={500}
            fontSize="sm"
            lineHeight={1.5}
            size="xs"
            _hover={{
              opacity: 0.8,
            }}
          >
            Deposit
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        maxW="480px"
        width="100%"
        borderRadius="20px"
        gap={6}
        display={'flex'}
        flexDirection={'column'}
        border="1px solid"
        borderColor="text.t4"
        bg="bg.t1"
        pb={5}
      >
        <DialogHeader borderBottom="1px solid" borderColor="text.t4">
          <DialogTitle color="text.t1" pt={5} pb={3} px={5} fontSize="md">
            Deposit
          </DialogTitle>
          <DialogCloseTrigger asChild right={3} top={3}>
            <ModalCloseIcon />
          </DialogCloseTrigger>
        </DialogHeader>
        <VStack w="full" px={5} gap={10}>
          <VStack w="full" gap={2}>
            <ArbitrumIcon />
            <Text fontWeight={500} fontSize="md" lineHeight={1.5}>
              Deposit USDC from Arbitrum
            </Text>
          </VStack>
          <VStack w="full" gap={3} align="start">
            <AmountInputArea
              amount={amount}
              setAmount={setAmount}
              balance={balance.toString()}
            />
            {Number(amount) < 5 && amount !== '' && (
              <Text fontSize={'xs'} lineHeight={'16px'} color={'fixed.short'}>
                The minimum deposit amount is 5 USDC.
              </Text>
            )}
          </VStack>
          <DialogFooter w="full">
            <Button
              size="xl"
              w="full"
              color="#06080B"
              bg="#AEFEC3"
              rounded={8}
              _hover={{ opacity: 0.8 }}
              loading={loading}
              disabled={Number(amount) < 5 || amount === ''}
              onClick={async () => {
                try {
                  setLoading(true);
                  await transfer(BigNumber(amount));
                  setOpen(false);
                } catch (e) {
                } finally {
                  setLoading(false);
                }
              }}
            >
              Deposit
            </Button>
          </DialogFooter>
        </VStack>
      </DialogContent>
    </DialogRoot>
  ) : null;
}
